import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  AppBar, Toolbar, Typography, Chip, IconButton, useTheme,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BuildIcon from "@mui/icons-material/Build";
import SettingsIcon from "@mui/icons-material/Settings";
import CircleIcon from "@mui/icons-material/Circle";
import MenuIcon from "@mui/icons-material/Menu";
import { getHealth } from "../api/apiService";

const DRAWER_WIDTH = 240;

const navItems = [
  { label: "Dashboard", path: "/", icon: <DashboardIcon /> },
  { label: "Tools", path: "/tools", icon: <BuildIcon /> },
  { label: "Settings", path: "/settings", icon: <SettingsIcon /> },
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [health, setHealth] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    getHealth()
      .then(setHealth)
      .catch(() => setHealth({ status: "error" }));
    const interval = setInterval(() => {
      getHealth().then(setHealth).catch(() => setHealth({ status: "error" }));
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const connected = health && health.status === "ok";

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Typography
          variant="h6"
          sx={{
            background: "linear-gradient(135deg, #6c5ce7, #a29bfe)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: 700,
          }}
        >
          WhizzC MCP
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Model Context Protocol
        </Typography>
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider", display: "flex", alignItems: "center", gap: 1 }}>
        <CircleIcon sx={{ fontSize: 10, color: connected ? "success.main" : "error.main" }} />
        <Typography variant="caption" color="text.secondary">
          {connected ? `${health.tools} tools` : "Disconnected"}
        </Typography>
      </Box>

      <List sx={{ flex: 1, pt: 1 }}>
        {navItems.map((item) => (
          <ListItemButton
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => { navigate(item.path); setMobileOpen(false); }}
            sx={{
              mx: 1, borderRadius: 1, mb: 0.5,
              "&.Mui-selected": {
                backgroundColor: "primary.main",
                color: "#fff",
                "& .MuiListItemIcon-root": { color: "#fff" },
                "&:hover": { backgroundColor: "primary.dark" },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
          </ListItemButton>
        ))}
      </List>

      <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Typography variant="caption" color="text.secondary">
          v0.1.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{ display: { xs: "block", md: "none" }, "& .MuiDrawer-paper": { width: DRAWER_WIDTH, bgcolor: "background.paper" } }}
      >
        {drawer}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{ display: { xs: "none", md: "block" }, "& .MuiDrawer-paper": { width: DRAWER_WIDTH, bgcolor: "background.paper", borderRight: 1, borderColor: "divider" } }}
      >
        {drawer}
      </Drawer>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", ml: { md: `${DRAWER_WIDTH}px` } }}>
        <AppBar position="sticky" elevation={0} sx={{ bgcolor: "background.paper", borderBottom: 1, borderColor: "divider" }}>
          <Toolbar>
            <IconButton sx={{ display: { md: "none" }, mr: 1 }} onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1, fontSize: 16 }}>
              {navItems.find((n) => n.path === location.pathname)?.label || "MCP"}
            </Typography>
            {connected && (
              <Chip
                size="small"
                icon={<CircleIcon sx={{ fontSize: "10px !important" }} />}
                label={`${health.tools} tools · ${health.integrations?.length || 0} integrations`}
                color="success"
                variant="outlined"
                sx={{ fontSize: 11 }}
              />
            )}
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
